import { useCallback, useEffect, useState } from 'react';

import { tokenStorage } from 'api/client/helpers/tokenStorage';
import { parseServerError } from 'api/helpers';
import {
  useLoginWithTwoFactor,
  useSetPassword as useSetPasswordApi,
  useSetPasswordValidate,
} from 'api/requests';
import { getValidationErrors } from 'helpers';
import { useTranslation } from 'libs/i18n';
import { useNotify } from 'libs/notify';

import { FormSubmit, FormErrors } from '../forms/passwordForm';

export const useSetPassword = (otpRequired: boolean) => {
  const notify = useNotify();
  const { t } = useTranslation();

  const { setPassword, error } = useSetPasswordApi();
  const { setPasswordValidate } = useSetPasswordValidate();

  const { loginWithTwoFactor } = useLoginWithTwoFactor();

  const [formErrors, setFormErrors] = useState<FormErrors>({});

  const [isTwoFactorAuthentication, setIsTwoFactorAuthentication] =
    useState(false);

  const [submitLoading, setSubmitLoading] = useState(false);

  const onBackPassword = useCallback(() => {
    setIsTwoFactorAuthentication(false);
  }, []);

  const onSubmit: FormSubmit = useCallback(
    async (values) => {
      if (otpRequired && !isTwoFactorAuthentication) {
        setSubmitLoading(true);
        try {
          await setPasswordValidate(values.password);
        } catch (error) {
          const serverError = parseServerError(error);
          if (!serverError) {
            notify.show('error', t('common.standardError'));
            setSubmitLoading(false);
            return false;
          }
          const validationErrors = getValidationErrors<'password'>(serverError);
          if (validationErrors?.password) {
            setFormErrors({
              password: validationErrors.password[0].description,
            });
          } else {
            notify.error(serverError);
            setFormErrors({ password: undefined });
          }
          setSubmitLoading(false);
          return false;
        }
        setSubmitLoading(false);
        setIsTwoFactorAuthentication(true);
        return false;
      }

      setSubmitLoading(true);

      if (otpRequired) {
        const loginData = await loginWithTwoFactor(values.otpCode!).catch(
          () => undefined
        );
        if (loginData) {
          tokenStorage.login(loginData);
          onBackPassword();
        } else {
          setFormErrors({ otpCode: 'error' });
          setSubmitLoading(false);
          return false;
        }
      }

      const res = await setPassword(values.password).catch(() => false);

      if (res) {
        window.location.replace('/');
      } else {
        setSubmitLoading(false);
      }

      return res;
    },
    [
      t,
      notify,
      otpRequired,
      isTwoFactorAuthentication,
      setPassword,
      setPasswordValidate,
      loginWithTwoFactor,
      onBackPassword,
    ]
  );

  // handle error
  useEffect(() => {
    if (error) {
      const validationErrors = getValidationErrors<'password'>(error);
      if (validationErrors?.password) {
        setFormErrors({ password: validationErrors.password[0].description });
      } else {
        notify.error(error);
        setFormErrors({ password: undefined });
      }
    } else {
      setFormErrors({ password: undefined });
    }
  }, [error, notify, setFormErrors]);

  return {
    submitLoading,
    formErrors,
    isTwoFactorAuthentication,
    onSubmit,
    onBackPassword,
  };
};
